/**
 * 분기 지도(표 기본) 모델 — 설계 `docs/2026-09-13-분기지도-표-구현설계-v1.md` §4.
 *
 * 입력: 후보 비교 모델(shadow·관세율표 항목·결정례·명칭 일치), 후보 호별 `heading-detail` 응답,
 *       (선택) 제안 LLM `map` 응답. 출력: 관세율표 배치의 행 목록 — 호 → 소호 → (군) → 국내 세번 —
 *       에 상태(on/open/off)와 "가르는 사실" 줄이 끼어 있는 구조.
 *
 * 규칙: LLM 후보·가지는 표에 그려지되 상태를 정하는 것은 shadow와 국내표(option_outcomes)다.
 *       shadow가 아무 말도 하지 않는 자리만 LLM 제안이 채우고 `source:"llm"`으로 표시된다.
 */
import { digitsOf, formatCode, headingFamilyQuestion, outcomeRelation, subheadingFamilyQuestion } from "./candidateCompareModel";

const ROW = Object.freeze({ HS4: "hs4", HS6: "hs6", GROUP: "group", LEAF: "leaf", Q: "q", NOTE: "note" });
export const ROW_KIND = ROW;

function rateSummary(rates) {
  const r = rates && typeof rates === "object" ? rates : {};
  const basic = r.A != null ? String(r.A) : "";
  const others = Object.entries(r).filter(([c]) => c !== "A" && !/^F/.test(c)).map(([c, v]) => `${c} ${v ?? ""}`.trim());
  return { basic, tan: others.slice(0, 2).join(" · ") + (others.length > 2 ? " …" : "") };
}

/** heading-detail 응답을 호 하나의 트리로 정리한다. national_trees가 있으면 군(group) 계층을 살린다. */
export function headingTree(detail) {
  if (!detail || !detail.hs4) return null;
  const rows = Array.isArray(detail.rows) ? detail.rows : [];
  const byHsk = new Map(rows.map((r) => [digitsOf(r.hsk), r]));
  const hs6Set = [...new Set(rows.map((r) => digitsOf(r.hsk).slice(0, 6)))].sort();
  const trees = detail.national_trees && typeof detail.national_trees === "object" ? detail.national_trees : {};
  const popKo = detail.subheading_popums_ko || {};
  const popEn = detail.subheading_popums_en || {};
  const strip = (s) => String(s || "").replace(/^\s*\d{4}\.\d{2}\s*-\s*/, "").trim();
  const subs = hs6Set.map((hs6) => {
    const leaves = rows.filter((r) => digitsOf(r.hsk).startsWith(hs6)).sort((a, b) => digitsOf(a.hsk).localeCompare(digitsOf(b.hsk)));
    const tree = Array.isArray(trees[hs6]) ? trees[hs6] : null;
    const children = [];
    const pushLeaf = (hsk, node, depth) => {
      const r = byHsk.get(digitsOf(hsk)) || {};
      const rate = rateSummary(r.rates);
      children.push({ kind: ROW.LEAF, hsk: digitsOf(hsk), tail: digitsOf(hsk).slice(6), ko: r.name_ko || node?.name_ko || "", en: r.popum || node?.name_en || "",
        basic: rate.basic, tan: rate.tan, importReq: !!r.has_import_req, exportReq: !!r.has_export_req, depth });
    };
    if (tree && tree.length) {
      const walk = (nodes, depth) => nodes.forEach((n) => {
        if (n.is_leaf) pushLeaf(n.hsk, n, depth);
        else { children.push({ kind: ROW.GROUP, code: String(n.prefix || "").slice(6) || "", ko: n.name_ko || n.label || "", en: n.name_en || "", depth }); walk(n.children || [], depth + 1); }
      });
      walk(tree, 0);
    } else leaves.forEach((r) => pushLeaf(r.hsk, r, 0));
    return { hs6, sub: hs6.slice(4), ko: strip(popKo[hs6]) || (leaves.length === 1 ? leaves[0].name_ko : ""), en: strip(popEn[hs6]) || "", children, leafCount: leaves.length };
  });
  return { hs4: String(detail.hs4), section: detail.section ? String(detail.section) : null, chapter: String(detail.chapter || String(detail.hs4).slice(0, 2)), ko: detail.heading_title_ko || "", en: detail.heading_title_en || "", subs, neighbors: Array.isArray(detail.neighboring_headings) ? detail.neighboring_headings.map(String) : [] };
}

/** 어느 후보(코드)와 맞는가: 후보 모델의 state를 코드 접두로 조회한다. */
function candidateFor(candidates, code) {
  const d = digitsOf(code);
  return candidates.find((c) => c.code === d) || candidates.find((c) => d.startsWith(c.code) || c.code.startsWith(d)) || null;
}

/**
 * 층별 "가르는 사실" — 질문의 option_outcomes가 이 층의 형제들을 실제로 가르면 그 질문을 그 층에 둔다.
 * hs4 아래: 소호가 다른 도달 코드가 둘 이상인 질문. hs6 아래: 도달 코드가 그 소호의 국내 잎에 걸리는 질문.
 */
export function splittingQuestions(groups, { hs4, hs6, headings = false }) {
  const all = [...(groups?.choices || []), ...(groups?.checklist || [])];
  const out = [];
  for (const item of all) {
    const options = item.kind === "choice" ? item.options.map((o) => ({ value: o.value, label: o.label, outcome: o.outcome }))
      : [{ value: "yes", label: "맞습니다", outcome: item.yesOutcome }, { value: "no", label: "아닙니다", outcome: item.noOutcome }];
    const withOutcome = options.filter((o) => o.outcome);
    if (!withOutcome.length) continue;
    const codes = withOutcome.flatMap((o) => o.outcome.codes.length ? o.outcome.codes : o.outcome.hs6);
    if (headings) { // 호 층: 도달 호가 둘 이상 갈리는 질문
      const h4 = new Set(codes.map((c) => c.slice(0, 4)));
      if (h4.size >= 2) out.push({ ...item, options, level: "hs4" });
    } else if (hs6) { // 소호 층: 도달 코드가 이 소호의 잎이고, 잎이 둘 이상 갈리는 질문
      const under = codes.filter((c) => c.length === 10 && c.startsWith(hs6));
      const distinct = new Set(under);
      if (under.length && distinct.size >= 2) out.push({ ...item, options, level: "kr10" });
      else if (under.length && withOutcome.some((o) => o.outcome.outcome === "outside_route") && item.kind === "choice") out.push({ ...item, options, level: "kr10" });
    } else if (hs4) { // 호 층: 도달 소호가 둘 이상 갈리는 질문
      const subs = new Set(codes.filter((c) => c.startsWith(hs4)).map((c) => c.slice(0, 6)));
      if (subs.size >= 2) out.push({ ...item, options, level: "hs6" });
    }
  }
  return out;
}

/** LLM 지도(proposal/map)가 이 코드를 후보로 말했는가 — 배지용 `{why, models}`. `exact`면 소호 자신·그 아래 10자리만(호는 접두). */
function llmHit(llmMap, code, exact = false) {
  const cands = llmMap?.candidates;
  if (!Array.isArray(cands) || !cands.length) return null;
  const d = digitsOf(code);
  const hit = cands.find((c) => { const h = digitsOf(c.code); return exact ? h.startsWith(d) : (h.startsWith(d) || d.startsWith(h)); });
  return hit ? { why: String(hit.condition || hit.name || hit.why || ""), models: Array.isArray(hit.models) ? hit.models : [] } : null;
}

/**
 * 표 행을 만든다. candidates = candidateCompareModel의 후보(state·reason·unreachable·nameMatch·precedents…),
 * groups = 질문 묶음(option_outcomes 포함), details = {hs4: headingTree}, llmMap = 검증된 map 응답(선택).
 */
export function buildBranchTable({ candidates = [], groups = null, details = {}, llmMap = null }) {
  const hs4s = [...new Set(candidates.map((c) => c.code.slice(0, 4)))];
  const rows = [];
  const headings = [];
  const liveCodes = candidates.filter((c) => c.state !== "excluded").map((c) => c.code);
  for (const hs4 of hs4s) {
    const tree = details[hs4] ? headingTree(details[hs4]) : null;
    const hs4Cands = candidates.filter((c) => c.code.startsWith(hs4));
    const anyLive = hs4Cands.some((c) => c.state !== "excluded");
    const anyOn = hs4Cands.some((c) => c.state === "path");
    const llmHeading = llmMap?.candidates?.find((c) => digitsOf(c.code).startsWith(hs4));
    const llmHere = llmHit(llmMap, hs4);
    const hist = hs4Cands.some((c) => c.history != null) ? hs4Cands.reduce((n, c) => n + (c.history || 0), 0) : null;
    const h = { hs4, chapter: tree?.chapter || hs4.slice(0, 2), section: tree?.section || null, ko: tree?.ko || llmHeading?.name || "", en: tree?.en || "", state: !anyLive ? "off" : anyOn ? "on" : "open",
      why: !anyLive ? (hs4Cands[0]?.reason || "") : "", source: hs4Cands.every((c) => c.source === "llm") ? "llm" : "tree", loaded: Boolean(tree),
      ev: (hist != null || hs4Cands.some((c) => c.llm) || hs4Cands.some((c) => c.origin) || llmHere) ? { history: hist, llm: hs4Cands.find((c) => c.llm)?.llm || llmHere || null, precedents: hs4Cands.some((c) => c.precedents != null) ? Math.max(...hs4Cands.map((c) => c.precedents || 0)) : undefined } : null,
      origin: hs4Cands.some((c) => c.origin) ? Object.assign({}, ...hs4Cands.map((c) => c.origin || {})) : null };
    headings.push(h);
    rows.push({ kind: ROW.HS4, ...h, id: `h-${hs4}` });
    // 호 아래 가르는 사실(소호를 가르는 질문)
    if (anyLive) splittingQuestions(groups, { hs4 }).forEach((q) => rows.push({ kind: ROW.Q, level: "hs6", id: `q-${hs4}-${q.key}`, key: q.key, prompt: q.prompt, options: q.options, answer: q.answer }));
    if (!tree) { rows.push({ kind: ROW.NOTE, id: `n-${hs4}`, text: anyLive ? `제${hs4}호 관세율표를 불러오고 있습니다.` : `제${hs4}호는 경로 밖입니다.` }); continue; }
    for (const s of tree.subs) {
      const cand = candidateFor(hs4Cands, s.hs6);
      // 후보가 호 자체(4자리)로 살아 있으면 그 아래 소호는 전부 미확정이다 — 답한 사실이 없는데 "배제"가 되면 안 된다(조미김 제20류 사례).
      const liveHere = hs4Cands.some((c) => c.state !== "excluded" && (c.code.startsWith(s.hs6) || (c.code.length === 4 && s.hs6.startsWith(c.code))));
      const pathHere = hs4Cands.some((c) => c.state === "path" && c.code.length >= 6 && c.code.startsWith(s.hs6));
      let state, why = "";
      if (!anyLive) { state = "off"; }
      else if (liveHere) { state = pathHere ? "on" : "open"; }
      else { state = "off"; why = cand?.reason || (hs4Cands.some((c) => c.state !== "excluded") ? "답한 사실이 가리키는 소호가 아닙니다" : ""); }
      const leafCands = hs4Cands.filter((c) => c.code.length === 10 && c.code.startsWith(s.hs6));
      const llmSub = llmHit(llmMap, s.hs6, true);
      rows.push({ kind: ROW.HS6, id: `s-${s.hs6}`, hs4, hs6: s.hs6, sub: s.sub, ko: s.ko, en: s.en, state, why, collapsed: state === "off",
        ev: cand || llmSub ? { precedents: cand?.precedents, history: cand?.history, nameMatch: cand?.nameMatch, nameVia: cand?.nameVia, llm: cand?.llm || llmSub || null } : null, origin: cand?.origin || null, leafCount: s.leafCount });
      // 경로 밖 소호의 잎도 행으로 남긴다(컴포넌트가 접고, "세번 보기"로 펼친다). 가르는 사실 줄은 살아 있는 소호에만.
      if (state !== "off") splittingQuestions(groups, { hs6: s.hs6 }).forEach((q) => rows.push({ kind: ROW.Q, level: "kr10", id: `q-${s.hs6}-${q.key}`, key: q.key, prompt: q.prompt, options: q.options, answer: q.answer, hs6: s.hs6 }));
      for (const ch of s.children) {
        if (ch.kind === ROW.GROUP) { rows.push({ kind: ROW.GROUP, id: `g-${s.hs6}-${ch.code}-${ch.ko.slice(0, 8)}`, hs6: s.hs6, code: ch.code, ko: ch.ko, en: ch.en, depth: ch.depth, state }); continue; }
        const lc = leafCands.find((c) => c.code === ch.hsk);
        let lstate = state === "on" ? "open" : state;
        let lwhy = "";
        if (lc) { lstate = lc.state === "path" ? "full" : lc.state === "excluded" ? "off" : "open"; if (lc.unreachable) { lstate = "unreachable"; lwhy = "현재 확인 계약의 어떤 답도 이 세번으로 가지 않습니다"; } }
        else if (leafCands.length && !leafCands.some((c) => c.code === ch.hsk)) { lstate = "off"; lwhy = "국내표가 가리키는 잎이 아닙니다"; }
        // 국내 층: 답한 사실로 이미 갈렸는가
        const krQs = state === "off" ? [] : splittingQuestions(groups, { hs6: s.hs6 });
        for (const q of krQs) { if (q.answer === undefined) continue; const opt = q.options.find((o) => o.value === q.answer); const rel = outcomeRelation(opt?.outcome, ch.hsk); if (rel === "other" || rel === "outside") { lstate = "off"; lwhy = lwhy || `${q.prompt.slice(0, 18)}… 답과 다름`; } else if (rel === "leads" && lstate !== "off") lstate = "full"; }
        rows.push({ kind: ROW.LEAF, id: `l-${ch.hsk}`, hs6: s.hs6, hsk: ch.hsk, tail: ch.tail, ko: ch.ko, en: ch.en, basic: ch.basic, tan: ch.tan, importReq: ch.importReq, exportReq: ch.exportReq, depth: ch.depth, state: lstate, why: lwhy,
          ev: lc ? { precedents: lc.precedents, history: lc.history, nameMatch: lc.nameMatch, nameVia: lc.nameVia, lead: lc.lead, llm: lc.llm || null } : null, origin: lc?.origin || null });
      }
    }
  }
  // LLM만 말한 후보(shadow에 없음) — 표 끝에 회색 점선 호로
  if (llmMap?.candidates) for (const c of llmMap.candidates) {
    const d = digitsOf(c.code); if (!d || hs4s.includes(d.slice(0, 4))) continue;
    rows.push({ kind: ROW.HS4, id: `h-${d.slice(0, 4)}`, hs4: d.slice(0, 4), chapter: d.slice(0, 2), ko: c.name || c.condition || "", en: "", state: "llm", source: "llm", why: c.condition || "", llmModels: Array.isArray(c.models) ? c.models : [], llmCode: d });
    headings.push({ hs4: d.slice(0, 4), ko: c.name || "", state: "llm", source: "llm" });
    hs4s.push(d.slice(0, 4));
  }
  const full = rows.find((r) => r.kind === ROW.LEAF && r.state === "full" && liveCodes.some((c) => c === r.hsk || (c.length === 6 && r.hsk.startsWith(c))));
  return { rows, headings, narrowed: full ? full.hsk : null, liveCount: liveCodes.length };
}

/** 표시용 코드. */
export const displayCode = formatCode;

/** 상태 문구 — 파랑·회색만. */
export function stateLabel(state) {
  return { on: "답과 일치", full: "좁혀짐 · 확정 아님", open: "미확정", off: "배제", unreachable: "이 규칙이 데려가지 않음", llm: "LLM 제안 · 트리 미인식" }[state] || "미확정";
}

/* ───────────────────────── 지도(흐름) 모델 ─────────────────────────
 * 열: 물품 → [호를 가르는 사실] → 후보 호 → [소호를 가르는 사실] → 소호 → [세번을 가르는 사실] → 국내 세번.
 * 표 행(buildBranchTable)을 그대로 마디로 옮기고, 호 층의 가르는 사실만 따로 만든다(방향 질문·요건).
 * 선(edge)은 선택지 → 마디, 마디 → 하위 마디, 물품 → 사실. 상태(on/off/open)는 답과 마디 상태에서 온다.
 * 판정은 없다. 후보를 골라도 저장하지 않고, "되려면" 목록의 버튼만 기존 키로 답을 저장한다.
 */
function parseRejectedDirection(text) {
  const m = String(text || "").match(/^\s*(\d{4,10})\s*[:：]\s*(.*?)(?:\s*[—-]\s*(.+?))?\s*(?:\((?:이 경로의 조건과 다름|.*?)\))?\s*$/);
  return m ? { code: m[1], label: (m[2] || "").trim(), valueLabel: (m[3] || "").trim() } : null;
}

/**
 * 호를 가르는 사실 — (a) option_outcomes가 호를 가르는 질문, (b) 방향 계약의 조건(조건부 방향 requirements·rejected_directions·
 * product_intake.conditions)에서 재구성한 사실. (b)는 선택지 라벨 ↔ 요건 라벨로 도달 호를 잇는다(값이 없으면 "→ ?").
 */
export function headingSplittingFacts({ groups, tree, answers = {}, optionLabels = {}, nodeCodes = [] }) {
  const out = [];
  const seen = new Set();
  // 류 공통 트리의 호 선택 질문 — 옵션이 곧 호다(라벨 앞 4자리). 추천은 표시만, 자동 답 없음.
  const family = headingFamilyQuestion(tree);
  if (family && family.options.some((o) => o.hs4)) {
    seen.add(family.key);
    const answer = answers[family.key] !== undefined && answers[family.key] !== null ? String(answers[family.key]) : undefined;
    // readOnly: 호는 상품 사실로 정해진다. 사용자가 호를 직접 고르는 답은 저장소(productFactsOnly)가 거르므로 여기서도 버튼을 만들지 않는다.
    out.push({ id: `fact:${family.key}`, key: family.key, prompt: family.prompt, level: "hs4", parent: null, answer, source: "tree", readOnly: true,
      options: family.options.map((o) => ({ value: o.value, label: o.label, outcome: null, targets: o.hs4 ? [o.hs4] : [], recommended: family.recommended.includes(o.value) })) });
  }
  for (const q of splittingQuestions(groups, { headings: true })) {
    seen.add(q.key);
    out.push({ id: `fact:${q.key}`, key: q.key, prompt: q.prompt, level: "hs4", parent: null, answer: q.answer, source: "outcomes",
      options: q.options.map((o) => ({ value: o.value, label: o.label, outcome: o.outcome, targets: o.outcome ? (o.outcome.codes.length ? o.outcome.codes : o.outcome.hs6) : [] })) });
  }
  const d = tree?.product_fact_dialogue || {};
  const cond = d.conditional_assessment || {};
  const liveCands = Array.isArray(cond.candidates) ? cond.candidates : [];
  // 철회 문구는 조건부 방향(conditional_assessment)에 있거나, 전부 철회되면 product_intake.withdrawal_reasons 에만 있다.
  const rejected = [...(Array.isArray(cond.rejected_directions) ? cond.rejected_directions : []),
    ...(Array.isArray(d.product_intake?.withdrawal_reasons) ? d.product_intake.withdrawal_reasons : [])].map(parseRejectedDirection).filter(Boolean);
  const conditions = Array.isArray(d.product_intake?.conditions) ? d.product_intake.conditions : [];
  const assumptions = Array.isArray(cond.assumptions) ? cond.assumptions : [];
  // 방향을 가르는 키: 요건에 등장하는 키 ∪ direction_* ∪ "검토 경로" 문구가 붙은 미답 질문
  const keys = new Map(); // key -> prompt
  liveCands.forEach((c) => (c.requirements || []).forEach((r) => { if (r?.fact_key) keys.set(r.fact_key, r.label || r.fact_key); }));
  conditions.forEach((c) => { if (c?.fact_key && String(c.fact_key).startsWith("direction_")) keys.set(c.fact_key, c.prompt_ko || c.fact_key); });
  assumptions.forEach((a) => { if (a?.fact_key && String(a.fact_key).startsWith("direction_")) keys.set(a.fact_key, a.label || a.fact_key); });
  (groups?.choices || []).forEach((c) => { if ((String(c.key).startsWith("direction_") || /검토 경로/.test(String(c.hint || ""))) && !keys.has(c.key)) keys.set(c.key, c.prompt); });
  for (const [key, prompt] of keys) {
    if (seen.has(key)) continue;
    seen.add(key);
    const choice = (groups?.choices || []).find((c) => c.key === key);
    const condRow = conditions.find((c) => c.fact_key === key);
    const answer = answers[key] !== undefined && answers[key] !== null ? String(answers[key]) : (condRow?.value != null ? String(condRow.value) : undefined);
    const labels = { ...(optionLabels[key] || {}) };
    if (choice) choice.options.forEach((o) => { labels[o.value] = labels[o.value] || o.label; });
    const assumed = assumptions.find((a) => a.fact_key === key);
    if (answer !== undefined && !labels[answer]) labels[answer] = assumed?.value_label || condRow?.value_label || answer;
    const values = Object.keys(labels).filter((v) => !["unknown", "undetermined"].includes(v));
    if (!values.length) continue;
    // 요건 라벨("부직포 / 펠트") ↔ 선택지 라벨
    const targetsOf = (label) => {
      const t = [];
      for (const c of liveCands) for (const r of c.requirements || []) {
        if (r.fact_key !== key) continue;
        const req = String(r.required || "").split(/\s*\/\s*/).map((x) => x.trim()).filter(Boolean);
        if (req.some((x) => x === label || label.includes(x) || x.includes(label))) t.push(digitsOf(c.code));
      }
      return [...new Set(t)];
    };
    const options = values.map((v) => ({ value: v, label: labels[v], outcome: null, targets: targetsOf(labels[v]) }));
    // 이 사실 때문에 떨어진 호(철회 문구의 질문 라벨 또는 답 라벨이 이 사실과 같을 때)
    const excludes = rejected.filter((r) => (r.label && r.label === prompt) || (answer !== undefined && r.valueLabel && r.valueLabel === labels[answer])).map((r) => digitsOf(r.code));
    // 답한 값이 요건 라벨에 못 걸렸으면 살아 있는 후보 전부로 잇는다 — 그 답이 이 방향을 유지시킨 것이므로.
    if (answer !== undefined) { const me = options.find((o) => o.value === answer); if (me && !me.targets.length && liveCands.length) me.targets = liveCands.map((c) => digitsOf(c.code)); }
    out.push({ id: `fact:${key}`, key, prompt, level: "hs4", parent: null, answer, source: "direction", options, excludes: [...new Set(excludes)].filter((c) => nodeCodes.some((n) => n.startsWith(c) || c.startsWith(n))) });
  }
  return out;
}

/**
 * 선택지 도달 표시. 두 자리는 류(제NN류), 이어지는 류는 묶는다(제50~66류) — 류 주가 '제11부 전체'처럼 류 범위를 가리킬 때 잘리지 않게.
 * 호·소호·세번은 셋까지만 보이고 나머지는 "…".
 */
export function targetLabel(codes) {
  const chapters = codes.filter((t) => t.length === 2).map(Number).sort((a, b) => a - b);
  const runs = [];
  for (const c of chapters) {
    const last = runs[runs.length - 1];
    if (last && (c === last[1] + 1 || (c === 78 && last[1] === 76))) last[1] = c; else runs.push([c, c]);
  }
  const pad = (n) => String(n).padStart(2, "0");
  const chapterText = runs.map(([a, b]) => (a === b ? `제${pad(a)}류` : `제${pad(a)}~${pad(b)}류`));
  const others = codes.filter((t) => t.length !== 2);
  const otherText = others.slice(0, 3).map(displayCode);
  return [...chapterText, ...otherText].join(" / ") + (others.length > 3 ? " …" : "");
}

const optState = (fact, o, nodeStateOf) => {
  if (fact.answer !== undefined) return fact.answer === o.value ? "on" : "off";
  if (o.outcome?.outcome === "outside_route") return "open";
  const st = o.targets.map(nodeStateOf).filter(Boolean);
  if (st.length && st.every((x) => x === "off")) return "off";
  return "open";
};

/**
 * 지도 모델. candidates·groups·details는 표와 같고, tree·answers·optionLabels는 호 층 사실 재구성에 쓴다.
 * 반환: { product, columns:[{kind:"root"|"facts"|"nodes", level, title}], nodes, facts, edges, narrowed, liveCount, summary }.
 */
export function buildBranchMap({ candidates = [], groups = null, details = {}, tree = null, answers = {}, optionLabels = {}, llmMap = null, query = "" }) {
  const table = buildBranchTable({ candidates, groups, details, llmMap });
  const rows = table.rows;
  const nodes = { hs4: [], hs6: [], kr10: [] };
  const facts = { hs4: [], hs6: [], kr10: [] };
  let curHs4 = null, curHs6 = null, curGroup = null;
  for (const r of rows) {
    if (r.kind === ROW.HS4) { curHs4 = r.hs4; curHs6 = null; curGroup = null; nodes.hs4.push({ id: `node:${r.hs4}`, code: r.hs4, display: r.hs4, level: "HS4", ko: r.ko, en: r.en, state: r.state, why: r.why, source: r.source, parent: null, loaded: r.loaded !== false, ev: r.ev || null, origin: r.origin || null, llmModels: r.llmModels || null, llmCode: r.llmCode || null }); continue; }
    if (r.kind === ROW.HS6) { curHs6 = r.hs6; curGroup = null; nodes.hs6.push({ id: `node:${r.hs6}`, code: r.hs6, display: displayCode(r.hs6), level: "HS6", ko: r.ko, en: r.en, state: r.state, why: r.why, parent: r.hs4, ev: r.ev, origin: r.origin || null, leafCount: r.leafCount, collapsed: r.state === "off" }); continue; }
    if (r.kind === ROW.GROUP) { curGroup = r.ko; continue; }
    if (r.kind === ROW.LEAF) { nodes.kr10.push({ id: `node:${r.hsk}`, code: r.hsk, display: displayCode(r.hsk), level: "KR10", ko: r.ko, en: r.en, group: curGroup, state: r.state, why: r.why, parent: r.hs6, ev: r.ev ? { ...r.ev, basic: r.basic } : { basic: r.basic }, origin: r.origin || null, basic: r.basic, tan: r.tan, importReq: r.importReq }); continue; }
    if (r.kind === ROW.Q) {
      const level = r.level === "kr10" ? "kr10" : "hs6";
      const parent = level === "kr10" ? (r.hs6 || curHs6) : curHs4;
      const bucket = facts[level];
      if (bucket.some((f) => f.key === r.key && f.parent === parent)) continue;
      bucket.push({ id: `fact:${r.key}:${parent}`, key: r.key, prompt: r.prompt, level, parent, answer: r.answer, source: "outcomes",
        options: r.options.map((o) => ({ value: o.value, label: o.label, outcome: o.outcome, targets: o.outcome ? (o.outcome.codes.length ? o.outcome.codes : o.outcome.hs6) : [] })) });
    }
  }
  // 류 공통 트리의 소호 선택 질문 — 라벨 앞 "9102.11"이 소호. 옵션이 여러 호에 걸치면 호마다 그 호의 옵션만 싣는다.
  // 호가 정해진 뒤에만 그 호 아래에 둔다(호 선택 답이 가리키는 호, 또는 살아 있는 호가 하나뿐일 때). 호가 여럿 열려 있으면 소호 열은 아직 없다.
  const subFamily = subheadingFamilyQuestion(tree);
  if (subFamily) {
    const answer = answers[subFamily.key] !== undefined && answers[subFamily.key] !== null ? String(answers[subFamily.key]) : undefined;
    const family = headingFamilyQuestion(tree);
    const chosen = family && answers[family.key] != null ? family.options.find((o) => o.value === String(answers[family.key]))?.hs4 || null : null;
    const liveHs4 = nodes.hs4.filter((n) => n.state !== "off" && n.state !== "llm");
    for (const h of nodes.hs4) {
      if (!(chosen ? h.code === chosen : liveHs4.length === 1 && liveHs4[0].code === h.code)) continue;
      const opts = subFamily.options.filter((o) => o.hs6 && o.hs6.startsWith(h.code));
      if (opts.length < 2 || facts.hs6.some((f) => f.key === subFamily.key && f.parent === h.code)) continue;
      facts.hs6.push({ id: `fact:${subFamily.key}:${h.code}`, key: subFamily.key, prompt: subFamily.prompt, level: "hs6", parent: h.code, answer, source: "tree", readOnly: true,
        options: opts.map((o) => ({ value: o.value, label: o.label, outcome: null, targets: [o.hs6], recommended: subFamily.recommended.includes(o.value) })) });
    }
  }
  const allNodes = [...nodes.hs4, ...nodes.hs6, ...nodes.kr10];
  const nodeByCode = new Map(allNodes.map((n) => [n.code, n]));
  const bySpecific = [...allNodes].sort((a, b) => b.code.length - a.code.length);
  const resolveNode = (code) => { const d = digitsOf(code); return nodeByCode.get(d) || bySpecific.find((x) => d.startsWith(x.code)) || allNodes.find((x) => x.code.startsWith(d)) || null; };
  const nodeStateOf = (code) => { const n = resolveNode(code); return n ? n.state : null; };
  facts.hs4 = headingSplittingFacts({ groups, tree, answers, optionLabels, nodeCodes: allNodes.map((n) => n.code) });
  // LLM 지도(proposal/map)의 가지 — 트리 질문 키에 매핑된 가지는 그 키로 답할 수 있고(저장은 기존 키), 매핑 안 된 가지는 읽기 전용 "참고".
  // 같은 키의 트리 사실이 이미 있으면 트리가 이긴다(LLM 가지는 배지로만). 도달 세번이 한 호의 소호들이면 소호 열에, 아니면 호 열에 둔다.
  const llmBranches = Array.isArray(llmMap?.branches) ? llmMap.branches : [];
  llmBranches.forEach((b, i) => {
    const existing = b.mapped && b.key ? [...facts.hs4, ...facts.hs6, ...facts.kr10].find((f) => f.key === b.key) : null;
    if (existing) { existing.llmModels = b.models || []; return; }
    const targets = b.options.flatMap((o) => o.leadsTo || []).map(digitsOf);
    const hs4Set = new Set(targets.map((t) => t.slice(0, 4)));
    const parent = hs4Set.size === 1 && targets.some((t) => t.length >= 6) && nodeByCode.has([...hs4Set][0]) ? [...hs4Set][0] : null;
    const level = parent ? "hs6" : "hs4";
    const key = b.mapped && b.key ? b.key : `llm:${i}`;
    const answer = b.mapped && answers[key] !== undefined && answers[key] !== null ? String(answers[key]) : undefined;
    facts[level].push({ id: `fact:${key}:${parent || "root"}`, key, prompt: b.label, level, parent, answer, source: "llm", models: b.models || [], mapped: Boolean(b.mapped), readOnly: !b.mapped,
      options: b.options.map((o) => ({ value: o.value != null ? o.value : o.label, label: o.label, outcome: null, targets: (o.leadsTo || []).map(digitsOf), models: o.models || [], unmapped: b.mapped && o.value == null })) });
  });
  // 선택지 상태·표시
  const decorate = (f) => {
    f.options.forEach((o) => {
      o.id = `opt:${f.key}:${o.value}`;
      o.state = optState(f, o, nodeStateOf);
      if (o.outcome?.outcome === "outside_route") o.to = "규칙 밖";
      else if (!o.targets.length) o.to = "?";
      else { const shown = [...new Set(o.targets.map((t) => (t.length === 10 && f.level !== "kr10") ? t.slice(0, 6) : t))]; o.to = targetLabel(shown); }
      if (o.outcome?.review) o.to = o.targets.length ? `${o.to} · 검토 보류` : "검토 보류";
    });
    return f;
  };
  Object.values(facts).forEach((list) => list.forEach(decorate));

  const edges = [];
  const edge = (from, to, state) => { if (from && to && from !== to && !edges.some((e) => e.from === from && e.to === to)) edges.push({ id: `${from}>${to}`, from, to, state }); };
  const stateOfNode = (n) => (n.state === "on" || n.state === "full" ? "on" : n.state === "off" ? "off" : "open");
  const targeted = new Set();
  const wireFact = (f) => {
    f.options.forEach((o) => {
      for (const t of o.targets) {
        let n = resolveNode(t); if (!n) continue;
        if (f.level === "hs6" && n.level === "KR10") n = nodeByCode.get(n.parent) || n;
        if (f.level === "hs4" && n.level === "KR10") n = nodeByCode.get(n.parent) || nodeByCode.get(n.code.slice(0, 4)) || n;
        edge(o.id, n.id, o.state === "on" ? "on" : o.state === "off" ? "off" : stateOfNode(n)); targeted.add(n.id);
      }
    });
    (f.excludes || []).forEach((c) => { const n = resolveNode(c); if (n) { edge(f.id, n.id, "off"); targeted.add(n.id); } });
  };
  // 물품 → 호 사실 / 호 마디
  if (facts.hs4.length) { facts.hs4.forEach((f) => { edge("root", f.id, f.answer !== undefined ? "on" : "open"); wireFact(f); }); nodes.hs4.forEach((n) => { if (!targeted.has(n.id)) edge("root", n.id, stateOfNode(n)); }); }
  else nodes.hs4.forEach((n) => edge("root", n.id, stateOfNode(n)));
  // 호 → 소호 사실 / 소호 마디
  nodes.hs4.forEach((h) => {
    const fs = facts.hs6.filter((f) => f.parent === h.code);
    fs.forEach((f) => { edge(h.id, f.id, stateOfNode(h) === "off" ? "off" : f.answer !== undefined ? "on" : "open"); wireFact(f); });
    nodes.hs6.filter((s) => s.parent === h.code).forEach((s) => { if (!fs.length || !targeted.has(s.id)) edge(h.id, s.id, stateOfNode(s)); });
  });
  // 소호 → 세번 사실 / 세번 마디
  nodes.hs6.forEach((s) => {
    const fs = facts.kr10.filter((f) => f.parent === s.code);
    fs.forEach((f) => { edge(s.id, f.id, stateOfNode(s) === "off" ? "off" : f.answer !== undefined ? "on" : "open"); wireFact(f); });
    nodes.kr10.filter((l) => l.parent === s.code).forEach((l) => { if (!fs.length || !targeted.has(l.id)) edge(s.id, l.id, stateOfNode(l)); });
  });

  const liveCount = table.liveCount;
  const narrowed = table.narrowed;
  const liveLeaves = nodes.kr10.filter((n) => n.state !== "off" && n.state !== "unreachable");
  const liveHs6 = nodes.hs6.filter((n) => n.state !== "off");
  const proposalOnly = candidates.length > 0 && candidates.every((c) => c.source === "proposal");
  if (proposalOnly) nodes.hs4.forEach((n) => { n.source = "proposal"; });
  const allOff = nodes.hs4.length > 0 && nodes.hs4.every((n) => n.state === "off" || n.state === "llm");
  // 관측 분해(제4·71·94류 등)가 상품 질문으로 호를 가르는 중이면 '인식 못 함'이 아니다.
  const decomposing = tree?.observable_derivation?.kind === "observable_decomposition";
  const decompChapter = tree?.chapter ? `제${String(tree.chapter).replace(/^0/, "")}류` : "결정트리";
  const summary = proposalOnly && decomposing ? `${decompChapter} 상품 질문에 답하는 중 · 오른쪽 후보는 제안 카드 신호(미확정)`
    : proposalOnly ? `결정트리 후보 없음 · 제안 카드 신호 ${candidates.length}개 · 전부 미확정`
    : allOff ? "검토 방향이 철회됨 · 답을 바꾸면 다시 열립니다"
    : narrowed ? `국내 세번까지 좁혀짐 · ${displayCode(narrowed)}`
    : liveHs6.length === 1 && liveLeaves.length > 1 ? `소호 하나 · ${displayCode(liveHs6[0].code)} · 국내 세번 ${liveLeaves.length}개 중`
      : liveCount ? `후보 ${liveCount}개 남음` : "후보 없음";
  const d = tree?.product_fact_dialogue || {};
  const product = { name: query || "", desc: [d.product_intake?.label, tree?.supported_chapter ? `제${tree.supported_chapter}류 결정트리` : null].filter(Boolean).join(" · ") || d.title || "" };
  const columns = [{ kind: "root", title: "물품" }];
  if (proposalOnly && decomposing && !facts.hs4.length && !llmBranches.length) columns.push({ kind: "note", level: "hs4", title: "호를 가르는 사실", text: `${decompChapter} 결정트리가 상품 질문으로 호를 가르는 중입니다. 위 "상품 정보를 확인해 주세요"에 답하면 호·소호·국내 세번이 정해지고 이 지도에 길이 이어집니다. 오른쪽 후보는 제안 카드의 신호(LLM 후보·국내 결정례·관세율표 명칭 일치·우리 사무소 이력)로 참고용이며 확정이 아닙니다.` });
  else if (proposalOnly && !facts.hs4.length && !llmBranches.length) columns.push({ kind: "note", level: "hs4", title: "호를 가르는 사실", text: "결정트리가 이 물품을 인식하지 못해 가르는 사실이 없습니다. 오른쪽 후보는 제안 카드의 신호(LLM 후보·국내 결정례·관세율표 명칭 일치·우리 사무소 이력)에서 왔고 확정이 아닙니다. 상품 설명을 넣으면(웹검증 또는 직접 입력) 결정트리가 다시 시도합니다. 아래 \"더 묻기\"로 물품을 설명하고 물어볼 수 있습니다." });
  else if (facts.hs4.length) columns.push({ kind: "facts", level: "hs4", title: "호를 가르는 사실" });
  columns.push({ kind: "nodes", level: "hs4", title: "후보 호" });
  if (facts.hs6.length) columns.push({ kind: "facts", level: "hs6", title: "소호를 가르는 사실" });
  if (nodes.hs6.length) columns.push({ kind: "nodes", level: "hs6", title: "소호" });
  if (facts.kr10.length) columns.push({ kind: "facts", level: "kr10", title: "국내 세번을 가르는 사실" });
  if (nodes.kr10.length || nodes.hs6.length) columns.push({ kind: "nodes", level: "kr10", title: "국내 세번" });
  // LLM 지도 패널(두 모델 교차 출력) — 서버 응답일 때만. 제안 카드에서 옮긴 llmMap(source:"proposal_card")에는 없다.
  const llm = llmMap && llmMap.models ? { summary: llmMap.summary || "", summaryModel: llmMap.summaryModel || null, models: llmMap.models, agreement: llmMap.agreement || { verdict: "single" },
    notThis: llmMap.notThis || [], checks: llmMap.checks || [], mostCommon: llmMap.mostCommon || {}, requestHash: llmMap.requestHash || null, branchCount: llmBranches.length, cacheHit: Boolean(llmMap.cache && llmMap.cache.hit) } : null;
  return { product, columns, nodes, facts, edges, narrowed, liveCount, summary, withdrawn: allOff, proposalOnly, llm, loaded: nodes.hs4.every((n) => n.loaded || n.source === "llm") };
}

/** 두 모델 일치 문구 — 파랑·회색만 쓰는 화면에 맞춘 짧은 한 줄. */
export function agreementLabel(llm) {
  if (!llm) return "";
  const a = llm.agreement || {};
  const g = llm.models?.gemini; const o = llm.models?.opus;
  const name = (m) => (m === "gemini" ? "Gemini" : "Opus");
  if (a.verdict === "single") { const who = a.answered || (g?.status === "ok" ? "gemini" : "opus"); const dead = who === "gemini" ? `Opus ${o?.reason || o?.status || "미응답"}` : `Gemini ${g?.reason || g?.status || "미응답"}`; return `${name(who)}만 답함 · ${dead}`; }
  if (a.verdict === "agree") return `Gemini·Opus 일치 · ${formatCode(a.geminiTop)}`;
  if (a.verdict === "top_agree") return `최다 후보 일치 · ${formatCode(a.geminiTop)} · 나머지 후보는 다름`;
  if (a.verdict === "partial") return `두 모델이 갈림 · Gemini ${formatCode(a.geminiTop)} / Opus ${formatCode(a.opusTop)} · 겹치는 후보 ${a.shared?.length || 0}개`;
  return `두 모델이 갈림 · Gemini ${formatCode(a.geminiTop)} / Opus ${formatCode(a.opusTop)} · 겹치는 후보 없음`;
}

/** 코드에서 조상까지의 마디 코드 목록(호·소호·세번). */
function ancestorsOf(code) { const d = digitsOf(code); const out = []; if (d.length >= 4) out.push(d.slice(0, 4)); if (d.length >= 6) out.push(d.slice(0, 6)); if (d.length === 10) out.push(d); return out; }

/** 선택지가 이 코드로 가는가(option_outcomes는 outcomeRelation, 요건 재구성은 targets). */
function optionLeads(o, code) {
  if (o.outcome) return outcomeRelation(o.outcome, code) === "leads";
  const d = digitsOf(code);
  return o.targets.some((t) => d.startsWith(t) || t.startsWith(d));
}

/**
 * 역검증 — 후보를 먼저 고르면 "이 세번이 되려면" 필요한 사실. 층마다, 그 후보로 가는 선택지가 전부가 아닌(가르는) 사실만.
 * status: ok(답이 그 값) · conflict(다른 값으로 답함) · open(미답). 후보 선택은 저장이 아니다.
 */
export function requiredFactsFor(map, code) {
  if (!map || !code) return [];
  const path = ancestorsOf(code);
  const onPath = (parent) => !parent || path.some((p) => p === parent || p.startsWith(parent));
  const out = [];
  for (const level of ["hs4", "hs6", "kr10"]) {
    for (const f of map.facts[level]) {
      if (!onPath(f.parent)) continue;
      const leading = f.options.filter((o) => optionLeads(o, code));
      if (!leading.length || leading.length === f.options.length) continue;
      const status = f.answer === undefined ? "open" : leading.some((o) => o.value === f.answer) ? "ok" : "conflict";
      out.push({ key: f.key, prompt: f.prompt, level, options: leading.map((o) => ({ value: o.value, label: o.label })), answer: f.answer,
        answerLabel: f.answer !== undefined ? (f.options.find((o) => o.value === f.answer)?.label || f.answer) : null, status, readOnly: Boolean(f.readOnly) });
    }
  }
  return out;
}

/** 선택한 후보로 가는 길 — 마디·선택지·사실·선 id 집합(강조용). */
export function pathFor(map, code) {
  const empty = { nodes: new Set(), options: new Set(), facts: new Set(), edges: new Set() };
  if (!map || !code) return empty;
  const anc = ancestorsOf(code);
  const nodeIds = new Set(anc.map((c) => `node:${c}`));
  const optIds = new Set();
  const factIds = new Set();
  for (const level of ["hs4", "hs6", "kr10"]) for (const f of map.facts[level]) {
    if (f.parent && !anc.some((p) => p === f.parent || p.startsWith(f.parent))) continue;
    const leading = f.options.filter((o) => optionLeads(o, code));
    if (leading.length && leading.length < f.options.length) { factIds.add(f.id); leading.forEach((o) => optIds.add(o.id)); }
  }
  const edgeIds = new Set(map.edges.filter((e) => (nodeIds.has(e.to) && (e.from === "root" || nodeIds.has(e.from) || optIds.has(e.from)))
    || (factIds.has(e.to) && (e.from === "root" || nodeIds.has(e.from)))).map((e) => e.id));
  return { nodes: nodeIds, options: optIds, facts: factIds, edges: edgeIds };
}
